import { z } from "zod";
import { stamps, IsoDate, Money, Uuid } from "./common.js";
import { matchState, pricingMode, pricingStatus, projectStatus } from "./enums.js";

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export const ProjectDto = z.object({
  id: Uuid,
  name: z.string(),
  clientDeveloper: z.string().nullable(),
  description: z.string().nullable(),
  status: projectStatus,
  ...stamps,
});
export type Project = z.infer<typeof ProjectDto>;

export const ProjectInput = z.object({
  name: z.string().min(1),
  clientDeveloper: z.string().nullish(),
  description: z.string().nullish(),
  status: projectStatus.default("active"),
});
export type ProjectInput = z.infer<typeof ProjectInput>;

// ---------------------------------------------------------------------------
// Pricings (named + dated runs inside a project)
// ---------------------------------------------------------------------------

export const PricingDto = z.object({
  id: Uuid,
  projectId: Uuid,
  name: z.string(),
  runDate: IsoDate,
  /** Optional historic "as at" date the pricing is computed against. */
  asAtDate: IsoDate.nullable(),
  status: pricingStatus,
  ...stamps,
});
export type Pricing = z.infer<typeof PricingDto>;

/** `POST /api/projects/:id/pricings` body (projectId comes from the path). */
export const PricingInput = z.object({
  name: z.string().min(1),
  runDate: IsoDate,
  asAtDate: IsoDate.nullish(),
});
export type PricingInput = z.infer<typeof PricingInput>;

// ---------------------------------------------------------------------------
// Filters (`PUT /api/pricings/:id/filters`)
// ---------------------------------------------------------------------------

/**
 * Pricing filter settings. Every field except `mode` has a sensible default,
 * so a bare `{ mode }` parses. Empty include/exclude arrays mean "no
 * restriction".
 */
export const PricingFiltersInput = z.object({
  mode: pricingMode,
  /** Only meaningful in `single_supplier` mode: force this supplier. */
  forcedSupplierId: Uuid.nullable().default(null),
  includeSupplierIds: z.array(Uuid).default([]),
  excludeSupplierIds: z.array(Uuid).default([]),
  includeBrandIds: z.array(Uuid).default([]),
  excludeBrandIds: z.array(Uuid).default([]),
  rateCardId: Uuid.nullable().default(null),
  branchId: Uuid.nullable().default(null),
  /** Reject prices older than this many days; null = no age limit. */
  maxPriceAgeDays: z.number().int().positive().nullable().default(null),
  /** Minimum line-match confidence (0-1) required to price a line. */
  minMatchConfidence: z.number().min(0).max(1).default(0),
  /** Whether unmatched lines are surfaced in totals as uncovered (vs excluded). */
  includeUnmatched: z.boolean().default(false),
  /** Compare per-each across pack sizes (BAG 100 vs EA vs LEN). On by default. */
  perUnitNormalisation: z.boolean().default(true),
  applyDiscounts: z.boolean().default(true),
  marginUpliftPct: z.number().min(0).default(0),
  /** Exact brand only when false; cheapest comparable across brands when true. */
  allowBrandSubstitution: z.boolean().default(true),
});
export type PricingFiltersInput = z.infer<typeof PricingFiltersInput>;

// ---------------------------------------------------------------------------
// Line items
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Line sources (import provenance — FR12b)
// ---------------------------------------------------------------------------

export const lineSourceKind = z.enum(["upload", "pasted", "manual"]);
export type LineSourceKind = z.infer<typeof lineSourceKind>;

/**
 * Metadata about where a buy-list import came from — extracted during parsing
 * (email headers, AI document reading) and freely editable by the user.
 */
export const SourceMetadata = z.object({
  /** Detected content kind (pdf, xlsx, eml, msg, image…). */
  sourceType: z.string().nullish(),
  /** Email subject / document title. */
  subject: z.string().nullish(),
  /** Who sent/issued the document (email sender, supplier on a PO…). */
  sender: z.string().nullish(),
  /** Date on/of the document (email sent date, PO date) — ISO date. */
  documentDate: z.string().nullish(),
  /** Document reference (PO number, order number, invoice number…). */
  reference: z.string().nullish(),
  notes: z.string().nullish(),
});
export type SourceMetadata = z.infer<typeof SourceMetadata>;

export const LineSourceDto = z.object({
  id: Uuid,
  pricingId: Uuid,
  kind: lineSourceKind,
  /** Original filename, "Pasted text", or "Manually added". */
  label: z.string(),
  /** Set when the original bytes are stored and downloadable. */
  hasFile: z.boolean(),
  metadata: SourceMetadata,
  ...stamps,
});
export type LineSource = z.infer<typeof LineSourceDto>;

/** `PATCH /api/pricings/:id/sources/:sourceId` body. */
export const SourceMetadataInput = SourceMetadata;
export type SourceMetadataInput = z.infer<typeof SourceMetadataInput>;

/** Candidate product surfaced for review on ambiguous/unmatched lines. */
export const CandidateProduct = z.object({
  productId: Uuid,
  description: z.string(),
  score: z.number(),
  /** Supplier product code (e.g. iQ code); optional on rows staged before it existed. */
  iqCode: z.string().nullable().optional(),
  brandName: z.string().nullable().optional(),
});
export type CandidateProduct = z.infer<typeof CandidateProduct>;

/** One labeled value captured from the source document's table row. */
export const SourceFieldPair = z.object({
  label: z.string(),
  value: z.string(),
});
export type SourceFieldPair = z.infer<typeof SourceFieldPair>;

export const LineItemDto = z.object({
  id: Uuid,
  pricingId: Uuid,
  /** 1-based position within the pasted/uploaded buy-list. */
  lineNo: z.number().int().positive(),
  /** Raw input line exactly as received. */
  rawInput: z.string(),
  parsedDescription: z.string().nullable(),
  requestedQty: z.number().nullable(),
  requestedUom: z.string().nullable(),
  matchedProductId: Uuid.nullable(),
  /** Match confidence 0-1. */
  matchConfidence: z.number().min(0).max(1).nullable(),
  matchState: matchState,
  /** How the match was made (e.g. "deterministic", "llm", "manual"). */
  matchMethod: z.string().nullable(),
  candidateProducts: z.array(CandidateProduct),
  /** Import this line came from; null on legacy rows staged before tracking. */
  sourceId: Uuid.nullable(),
  /** Labeled values from the source row (e.g. PRODUCT CODE / UNIT PRICE). */
  sourceFields: z.array(SourceFieldPair).nullable(),
  comparableGroupId: Uuid.nullable(),
  chosenSupplierId: Uuid.nullable(),
  /** Pins the exact historic price row used (FR2/FR14 traceability). */
  chosenPriceId: Uuid.nullable(),
  chosenNett: Money.nullable(),
  lineTotal: Money.nullable(),
  ...stamps,
});
export type LineItem = z.infer<typeof LineItemDto>;

/** `POST /api/pricings/:id/lines` body (paste variant; upload is multipart `file`). */
export const BuyListInput = z.object({
  text: z.string().min(1),
});
export type BuyListInput = z.infer<typeof BuyListInput>;

/** `POST /api/pricings/:id/lines/from-key` — file already PUT via presign. */
export const LinesFromKeyInput = z.object({
  uploadKey: z.string().min(1),
  filename: z.string().min(1),
});
export type LinesFromKeyInput = z.infer<typeof LinesFromKeyInput>;

/** 202 response for queued buy-list ingestion (matching continues async). */
export const QueuedIngest = z.object({
  sourceId: Uuid,
  queued: z.literal(true),
});
export type QueuedIngest = z.infer<typeof QueuedIngest>;

/**
 * `PATCH /api/pricings/:id/lines/:lineId` body — confirm/override a match, or
 * edit the line itself (qty/description). A description edit re-runs matching
 * for the line and clears its chosen price.
 */
export const LineOverrideInput = z.object({
  /** Set a product to confirm/override; explicit null clears the match. */
  matchedProductId: Uuid.nullable().optional(),
  chosenSupplierId: Uuid.nullable().optional(),
  requestedQty: z.number().positive().optional(),
  description: z.string().min(1).optional(),
});
export type LineOverrideInput = z.infer<typeof LineOverrideInput>;

/** `POST /api/pricings/:id/lines/add` body — append one line to the buy-list. */
export const AddLineInput = z.object({
  text: z.string().min(1),
});
export type AddLineInput = z.infer<typeof AddLineInput>;

/** `POST /api/pricings/:id/lines/delete` body — bulk-remove selected lines. */
export const DeleteLinesInput = z.object({
  lineIds: z.array(Uuid).min(1),
});
export type DeleteLinesInput = z.infer<typeof DeleteLinesInput>;

// ---------------------------------------------------------------------------
// Compute results (`POST /api/pricings/:id/compute`)
// ---------------------------------------------------------------------------

/** Whole-batch-to-one-supplier total for a single supplier. */
export const SingleSupplierResult = z.object({
  supplierId: Uuid,
  supplierName: z.string(),
  total: Money,
  /** Lines this supplier can price under the applied filters. */
  coveredLines: z.number().int().nonnegative(),
  uncoveredLines: z.number().int().nonnegative(),
});
export type SingleSupplierResult = z.infer<typeof SingleSupplierResult>;

/** Per-line pick in cheapest-per-line mode. */
export const CheapestPerLineLine = z.object({
  lineId: Uuid,
  supplierId: Uuid,
  nett: Money,
  lineTotal: Money,
});
export type CheapestPerLineLine = z.infer<typeof CheapestPerLineLine>;

/** Both strategies are always computed for comparison (FR13). */
export const ComputeResultDto = z.object({
  singleSupplier: z.array(SingleSupplierResult),
  cheapestPerLine: z.object({
    total: Money,
    lines: z.array(CheapestPerLineLine),
  }),
  /** Echo of the fully-defaulted filter settings the computation used. */
  appliedFilters: PricingFiltersInput,
});
export type ComputeResult = z.infer<typeof ComputeResultDto>;

// ---------------------------------------------------------------------------
// Pricing detail (`GET /api/pricings/:id`)
// ---------------------------------------------------------------------------

export const PricingDetailDto = PricingDto.extend({
  /** Saved filter settings; null until first set. */
  filterSettings: PricingFiltersInput.nullable(),
  /** Import provenance groups, oldest first. */
  sources: z.array(LineSourceDto),
  lines: z.array(LineItemDto),
  /** Last computed results; null until first compute. */
  results: ComputeResultDto.nullable(),
});
export type PricingDetail = z.infer<typeof PricingDetailDto>;
